import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, TrendingUp, TrendingDown, AlertTriangle, ChevronRight, Info } from "lucide-react";
import { api } from "../lib/api";
import { BottomNav } from "../components/BottomNav";
import { lsGet } from "../lib/safeStorage";
import { useI18n } from "../lib/i18n";

const CACHE_KEY = "habano.exchange.rates.v1";
const STALE_HOURS = 24;

function timeAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return ""; }
}
function fmtRate(r) { return r == null ? "—" : Number(r).toFixed(2); }
function isStale(iso) {
  try { return (Date.now() - new Date(iso).getTime()) > STALE_HOURS * 3600 * 1000; }
  catch { return true; }
}

function DirArrow({ prev, curr, testid }) {
  if (prev == null || curr == null) return null;
  if (curr > prev) return <TrendingUp className="w-4 h-4 text-gold" strokeWidth={1.8} data-testid={testid} />;
  if (curr < prev) return <TrendingDown className="w-4 h-4 text-red-400" strokeWidth={1.8} data-testid={testid} />;
  return null;
}

export default function ExchangeRatePage() {
  const { t } = useI18n();
  const [data, setData] = useState(() => {
    try { return JSON.parse(lsGet(CACHE_KEY) || "null"); } catch { return null; }
  });

  useEffect(() => {
    api.get("/cuba-exchange-rate").then(({ data: d }) => d && setData(d)).catch(() => {});
  }, []);

  const stale = data?.stale || (data?.rates?.[0] && isStale(data.rates[0].updatedAt));
  const lastUpd = data?.rates?.[0]?.updatedAt;

  return (
    <main data-testid="exchange-rate-page" className="min-h-screen pb-32 bg-background">
      <header className="px-5 pt-6 pb-3 flex items-center gap-3">
        <Link to="/" data-testid="exch-back" className="btn-ghost p-2"><ArrowLeft className="w-4 h-4" strokeWidth={1.5} /></Link>
        <div>
          <div className="eyebrow">{t("exch_eyebrow")}</div>
          <h1 className="font-display text-3xl mt-1 text-foreground" data-testid="exch-title">{t("exch_title")}</h1>
        </div>
      </header>

      {stale && (
        <div className="mx-5 mt-3 px-4 py-3 border border-amber-500/60 text-amber-300 text-[12px] inline-flex items-center gap-2 w-[calc(100%-2.5rem)]"
             data-testid="exch-stale-banner"
             style={{ background: "rgba(245,158,11,0.06)", minHeight: 48 }}>
          <AlertTriangle className="w-4 h-4 shrink-0" strokeWidth={1.5} />
          {t("exch_stale_msg")} ({timeAgo(lastUpd)})
        </div>
      )}

      {/* OFFICIAL RATES */}
      <section className="mx-5 mt-5 lux-card p-5" data-testid="exch-official">
        <div className="eyebrow">{t("exch_official_rates")}</div>
        <div className="mt-4 space-y-3">
          {(data?.rates || []).map((r) => (
            <div key={r.currency} className="flex items-center justify-between py-2 border-b border-[rgba(232,184,75,0.10)] last:border-0">
              <div className="min-w-0">
                <div className="font-display text-2xl text-foreground" data-testid={`exch-pair-${r.currency}`}>
                  {r.pair}
                </div>
                <div className="text-[11px] text-foreground/55 uppercase tracking-widest">{t("exch_segmento_iii")}</div>
              </div>
              <div className="text-right inline-flex items-center gap-2">
                <DirArrow prev={r.previous_rate} curr={r.rate} testid={`exch-detail-dir-${r.currency}`} />
                <span className="font-mono text-2xl text-gold" data-testid={`exch-rate-value-${r.currency}`}>
                  {fmtRate(r.rate)}
                </span>
              </div>
            </div>
          ))}
          {(!data?.rates || data.rates.length === 0) && (
            <div className="text-foreground/55 text-sm">{t("home_no_match")}</div>
          )}
        </div>
        <div className="mt-3 text-[11px] text-foreground/45">{t("exch_updated")} {timeAgo(lastUpd)}</div>
        <a
          href="https://eltoque.com/tasas-de-cambio-cuba/mercado-informal"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="exch-source-attribution"
          className="mt-1.5 inline-block"
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 11,
            color: "rgba(232,184,75,0.6)",
            textDecoration: "none",
          }}
        >
          {t("exch_source_attribution")}
        </a>
      </section>

      {/* INFORMAL */}
      {data?.informal?.length > 0 && (
        <section className="mx-5 mt-5 lux-card p-5" data-testid="exch-informal">
          <div className="eyebrow text-amber-300">{t("exch_estimate_only")}</div>
          {data.informal.map((r) => (
            <div key={r.currency} className="flex items-center justify-between mt-4">
              <div>
                <div className="font-display text-2xl text-foreground">{r.pair}</div>
                <div className="text-[11px] text-foreground/55 uppercase tracking-widest">Source: {r.source}</div>
              </div>
              <div className="font-mono text-2xl text-amber-300">{fmtRate(r.rate)}</div>
            </div>
          ))}
          <div className="mt-2 text-[11px] italic text-foreground/55">{data.informal[0]?.label}</div>
        </section>
      )}

      {/* INFORMAL-MARKET explainer */}
      <section className="mx-5 mt-5 lux-card p-5" data-testid="exch-explainer">
        <div className="inline-flex items-center gap-2 mb-2">
          <Info className="w-4 h-4 text-gold" strokeWidth={1.5} />
          <div className="font-display text-lg text-foreground">{t("exch_explainer_title")}</div>
        </div>
        <p className="text-[14px] leading-relaxed text-foreground/75">
          {t("exch_explainer_body")}
        </p>
      </section>

      {/* Exchange tip */}
      <section className="mx-5 mt-5 lux-card p-5" data-testid="exch-tip">
        <div className="font-display text-lg text-foreground">Exchange tip</div>
        <p className="mt-2 text-[14px] leading-relaxed text-foreground/75">
          For the best rate exchange money through your host or a trusted friend — never on the street.
        </p>
        <Link
          to="/tips/exchange"
          data-testid="exch-tip-link"
          className="mt-3 inline-flex items-center gap-1 text-[12px] uppercase tracking-widest text-gold"
          style={{ minHeight: 44 }}
        >
          Read the Money Exchange tip <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </Link>
      </section>

      <BottomNav />
    </main>
  );
}
